import { useState, useEffect, useEffectEvent } from 'react';
import { getLevelProtocol } from '../../utils/protocol';

interface InlineCountdownProps {
  level: number;
  onComplete: () => void;
  playCountBeep: (isLast: boolean) => void;
}

export default function InlineCountdown({ level, onComplete, playCountBeep }: InlineCountdownProps) {
  return <InlineCountdownSession key={level} level={level} onComplete={onComplete} playCountBeep={playCountBeep} />;
}

function InlineCountdownSession({ level, onComplete, playCountBeep }: InlineCountdownProps) {
  const [count, setCount] = useState(3);
  const showGo = count === 0;
  const proto = getLevelProtocol(level);

  const completeCountdown = useEffectEvent(() => onComplete());
  const playCountdownBeep = useEffectEvent((isLast: boolean) => playCountBeep(isLast));

  useEffect(() => {
    let remaining = 3;
    let completionTimeout: number | undefined;
    playCountdownBeep(false);

    const interval = window.setInterval(() => {
      remaining -= 1;
      setCount(remaining);
      if (remaining === 0) {
        clearInterval(interval);
        playCountdownBeep(true);
        completionTimeout = window.setTimeout(() => completeCountdown(), 400);
      } else {
        playCountdownBeep(remaining <= 1);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      clearTimeout(completionTimeout);
    };
  }, []);

  return (
    <div className="inline-countdown-overlay">
      <p
        className="font-mono"
        style={{
          fontSize: '0.6rem',
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          color: 'var(--text2)',
          marginBottom: '8px',
        }}
      >
        Level {level} Starting
      </p>
      <p
        className="font-mono"
        style={{
          fontSize: '0.75rem',
          color: 'var(--text2)',
          marginBottom: '24px',
        }}
      >
        {proto.spm} steps/min · {proto.bpm} BPM
      </p>
      <span
        className="font-serif"
        style={{
          fontSize: '8rem',
          lineHeight: 1,
          fontWeight: 700,
          color: showGo ? 'var(--text)' : 'var(--accent)',
          fontVariantNumeric: 'tabular-nums',
          transition: 'color 0.15s',
        }}
      >
        {showGo ? 'GO' : count}
      </span>
    </div>
  );
}
